import type { RuntimeParkedVehicleFamily } from
  "../world/schema/runtimeTile.ts";

export const VILNIUS_PARKED_VEHICLE_MANIFEST_URL =
  "/assets/vehicles/manifest.json?v=content-addressed-1";

export interface ParkedVehicleCollisionSpec {
  readonly id: string;
  readonly tileId: `${number}:${number}`;
  /** Ground contact at the centre of the parked vehicle. */
  readonly origin: Readonly<{ x: number; y: number; z: number }>;
  readonly rotationYRadians: number;
  /** Simplified body box; wheels remain visual-only. */
  readonly bodySizeM: Readonly<{ x: number; y: number; z: number }>;
}

export type RoadVehicleSeatRole = "driver" | "passenger";
export type RoadVehicleClass =
  | "car"
  | "service-van"
  | "fire-engine"
  | "city-bus"
  | "trolleybus";

export interface RoadVehicleMotionTuning {
  readonly maximumForwardSpeedMps: number;
  readonly maximumReverseSpeedMps: number;
  readonly accelerationMps2: number;
  readonly reverseAccelerationMps2: number;
  readonly brakingMps2: number;
  readonly coastingDragMps2: number;
  readonly maximumSteerRadians: number;
  readonly steeringResponseRadiansPerSecond: number;
  readonly steeringSelfCenterResponsePerSecond: number;
  readonly steeringFadeStartSpeedMps: number;
  readonly steeringFadeEndSpeedMps: number;
  readonly highSpeedSteeringScale: number;
  readonly maximumYawRateRadiansPerSecond: number;
  readonly handbrakeDecelerationMps2: number;
  readonly handbrakeSteeringScale: number;
}

export interface RoadVehicleSpec {
  readonly id: string;
  readonly label: string;
  readonly tileId: `${number}:${number}`;
  readonly initialPosition: Readonly<{ x: number; y: number; z: number }>;
  readonly initialYawRadians: number;
  readonly bodySizeM: Readonly<{ x: number; y: number; z: number }>;
  readonly wheelbaseM: number;
  readonly wheelRadiusM: number;
  readonly vehicleClass: RoadVehicleClass;
  readonly hasEmergencyEquipment: boolean;
  /**
   * Source-backed map parking uses a shared procedural body so every streamed
   * car can enter the normal road-vehicle pipeline without requiring one GLB
   * per source position. Authored vehicles leave this undefined.
   */
  readonly proceduralBodyColorHex?: number;
  readonly proceduralFamily?: RuntimeParkedVehicleFamily;
  readonly cameraTargetHeightOffsetM: number;
  readonly cameraDistanceM: number;
  readonly seats: readonly Readonly<{
    id: string;
    role: RoadVehicleSeatRole;
    localPosition: Readonly<{ x: number; y: number; z: number }>;
  }>[];
  readonly exits: readonly Readonly<{
    id: string;
    localPosition: Readonly<{ x: number; y: number; z: number }>;
  }>[];
}

/** User-requested top speed for passenger-car and emergency vehicle classes. */
export const ROAD_RESPONSE_VEHICLE_MAXIMUM_FORWARD_SPEED_KPH = 180;

/** Source placement boxes used by either static props or owned vehicle bodies. */
export const VILNIUS_PARKED_VEHICLE_COLLISIONS:
  readonly Readonly<ParkedVehicleCollisionSpec>[] = Object.freeze([
    parkedVehicle(
      "parked-vilnius-city-bus",
      { x: 522.247, y: 15, z: 331.565 },
      { x: 2.32, y: 2.47, z: 11.28 },
    ),
    parkedVehicle(
      "parked-vilnius-new-trolleybus",
      { x: 525.66, y: 15, z: 332.611 },
      { x: 2.32, y: 2.62, z: 11.28 },
    ),
    parkedVehicle(
      "parked-vilnius-old-trolleybus",
      { x: 529.073, y: 15, z: 333.787 },
      { x: 2.28, y: 2.58, z: 10.67 },
    ),
    parkedVehicle(
      "parked-hybrid-liftback",
      { x: 532.107, y: 15, z: 334.805 },
      { x: 1.57, y: 0.95, z: 4.09 },
    ),
    parkedVehicle(
      "parked-german-sports-sedan",
      { x: 534.382, y: 15, z: 335.568 },
      { x: 1.60, y: 0.92, z: 4.24 },
    ),
    parkedVehicle(
      "cathedral-police-patrol-wagon",
      { x: 594.6, y: 15, z: 345 },
      { x: 1.61, y: 0.94, z: 4.23 },
      3.141593,
    ),
    parkedVehicle(
      "cathedral-police-support-van",
      { x: 598.2, y: 15, z: 345 },
      { x: 1.79, y: 1.31, z: 4.55 },
      3.141593,
    ),
    parkedVehicle(
      "cathedral-ambulance",
      { x: 602.2, y: 15, z: 345 },
      { x: 1.80, y: 1.76, z: 5.39 },
      3.141593,
    ),
    parkedVehicle(
      "cathedral-fire-rescue-engine",
      { x: 607.2, y: 15, z: 345 },
      { x: 2.28, y: 2.51, z: 6.86 },
      3.141593,
    ),
  ]);

/**
 * Provisional user-facing driving values. These are deliberately isolated for
 * playtest tuning; neither the simulation nor renderer owns feel constants.
 */
export const ROAD_VEHICLE_TUNING = Object.freeze({
  interactionRadiusM: 3.15,
  minimumExitSpeedMps: 0.45,
  directionChangeThresholdMps: 0.2,
  maximumCurbStepM: 0.18,
  maximumGroundTiltRadians: 0.42,
  groundProbeHeightM: 2.4,
  groundProbeDepthM: 4.8,
  collisionSkinM: 0.04,
  roadGripScale: 1,
  hardscapeGripScale: 0.86,
  terrainGripScale: 0.58,
  fullWetGripScale: 0.68,
  fullWetBrakingScale: 0.74,
  recoveryMaximumSpeedMps: 0.75,
  recoveryLiftM: 0.12,
  recoveryDamageReduction01: 0.2,
  impactDamageSpeedMps: 5.5,
  maximumDamageImpactSpeedMps: 22,
  maximumVisualBodyPitchRadians: 0.045,
  maximumVisualBodyRollRadians: 0.065,
  bodyMotionResponsePerSecond: 7.5,
  suspensionTravelM: 0.11,
  indicatorPeriodSeconds: 0.78,
  doorTransitionSeconds: 0.46,
  occupantTransitionSeconds: 0.68,
  impactFeedbackSeconds: 0.42,
  motionByClass: Object.freeze({
    car: motionTuning(
      ROAD_RESPONSE_VEHICLE_MAXIMUM_FORWARD_SPEED_KPH / 3.6,
      5.5,
      3.8,
      3.6,
      14,
      1.25,
      0.58,
      3.3,
      3.4,
      90 / 3.6,
      150 / 3.6,
      0.36,
      1.05,
      17,
      0.72,
    ),
    "service-van": motionTuning(
      ROAD_RESPONSE_VEHICLE_MAXIMUM_FORWARD_SPEED_KPH / 3.6,
      4.8,
      2.8,
      2.8,
      11.5,
      1,
      0.52,
      2.5,
      2.8,
      75 / 3.6,
      135 / 3.6,
      0.3,
      0.85,
      14,
      0.68,
    ),
    "fire-engine": motionTuning(
      ROAD_RESPONSE_VEHICLE_MAXIMUM_FORWARD_SPEED_KPH / 3.6,
      3.4,
      1.7,
      1.8,
      9,
      0.75,
      0.44,
      1.8,
      2.2,
      60 / 3.6,
      110 / 3.6,
      0.24,
      0.6,
      11,
      0.62,
    ),
    "city-bus": motionTuning(
      11.1, 2.8, 1.9, 1.4, 5.8, 0.75, 0.43, 1.5,
      2.1, 30 / 3.6, 40 / 3.6, 0.42, 0.35, 7.2, 0.58,
    ),
    trolleybus: motionTuning(
      10.3, 2.6, 1.75, 1.3, 5.5, 0.7, 0.42, 1.45,
      2, 28 / 3.6, 37 / 3.6, 0.4, 0.32, 7, 0.56,
    ),
  } satisfies Readonly<Record<RoadVehicleClass, RoadVehicleMotionTuning>>),
});

/** Provisional authored-vehicle presentation values, isolated for playtest tuning. */
export const ROAD_VEHICLE_VISUAL_TUNING = Object.freeze({
  steeringWheelRatio: 2.2,
  maximumDoorOpenRadians: 1.08,
  headlightEmissiveIntensity: 4.5,
  runningTailEmissiveIntensity: 0.75,
  brakeTailEmissiveIntensity: 5.5,
  reverseLampEmissiveIntensity: 4,
  indicatorEmissiveIntensity: 5,
  beaconEmissiveIntensity: 7,
  headlightWorldIntensity: 18,
  headlightDistanceM: 32,
  headlightAngleRadians: 0.34,
  beaconWorldIntensity: 4.5,
  beaconDistanceM: 16,
  damagePatchMaximumOpacity: 0.42,
  impactParticleCount: 8,
});

export const ROAD_VEHICLE_CAMERA_TUNING = Object.freeze({
  fullEffectSpeedMps: 42,
  maximumPullbackM: 4.2,
  maximumLookAheadM: 6.5,
  maximumFovIncreaseDegrees: 11,
  planarFollowResponsePerSecond: 5.2,
});

export const ROAD_VEHICLE_EMERGENCY_YIELD_TUNING = Object.freeze({
  residentYieldRadiusM: 10,
  maximumResidentStepAsideM: 1.65,
});

/** Streaming limits for source-positioned, individually drivable parked cars. */
export const MAPPED_ROAD_VEHICLE_STREAMING = Object.freeze({
  interactiveActivationRadiusM: 42,
  visualActivationRadiusM: 220,
  visualFocusRefreshDistanceM: 16,
  retainedActivationRadiusM: 160,
  maximumNearbyPhysicsBodies: 80,
});

/** Visible wheel centres sit just inside the body's outer side faces. */
export const ROAD_VEHICLE_WHEEL_HALF_TRACK_BODY_SCALE = 0.47;

/**
 * The mapped-car tyres deliberately use ten-sided cylinders. Their resting
 * facet falls slightly short of the nominal radius, so sink the visual centre
 * by a restrained amount to keep the contact patch on the road at zero speed.
 */
export const MAPPED_ROAD_VEHICLE_TIRE_VISUAL_SINK_M = 0.018;

/**
 * Local-space support points used by vehicle grounding. Keeping this derived
 * from the same wheel geometry as the renderer prevents a kerb from lifting a
 * visible tyre whose physics probe still sits inboard on the pavement.
 */
export function roadVehicleGroundContactHalfExtents(
  spec: Readonly<Pick<RoadVehicleSpec, "bodySizeM" | "wheelbaseM">>,
): Readonly<{ x: number; z: number }> {
  return Object.freeze({
    x: spec.bodySizeM.x * ROAD_VEHICLE_WHEEL_HALF_TRACK_BODY_SCALE,
    z: spec.wheelbaseM / 2,
  });
}

export interface MappedRoadVehicleFamilyProfile {
  readonly bodySizeM: Readonly<{ x: number; y: number; z: number }>;
  readonly wheelbaseM: number;
  readonly wheelRadiusM: number;
  readonly cabinLengthScale: number;
  readonly cabinHeightM: number;
  readonly cabinOffsetZ: number;
  readonly roofLengthScale: number;
}

export const MAPPED_ROAD_VEHICLE_FAMILY_PROFILES: Readonly<
  Record<RuntimeParkedVehicleFamily, MappedRoadVehicleFamilyProfile>
> = Object.freeze({
  "compact-hatch": Object.freeze({
    bodySizeM: Object.freeze({ x: 1.74, y: 1.31, z: 4.08 }),
    wheelbaseM: 2.53,
    wheelRadiusM: 0.3,
    cabinLengthScale: 0.55,
    cabinHeightM: 0.53,
    cabinOffsetZ: -0.08,
    roofLengthScale: 0.48,
  }),
  "estate-wagon": Object.freeze({
    bodySizeM: Object.freeze({ x: 1.82, y: 1.34, z: 4.72 }),
    wheelbaseM: 2.79,
    wheelRadiusM: 0.32,
    cabinLengthScale: 0.58,
    cabinHeightM: 0.52,
    cabinOffsetZ: -0.12,
    roofLengthScale: 0.54,
  }),
  "ordinary-sedan": Object.freeze({
    bodySizeM: Object.freeze({ x: 1.8, y: 1.28, z: 4.55 }),
    wheelbaseM: 2.7,
    wheelRadiusM: 0.31,
    cabinLengthScale: 0.49,
    cabinHeightM: 0.48,
    cabinOffsetZ: -0.04,
    roofLengthScale: 0.42,
  }),
  "small-crossover": Object.freeze({
    bodySizeM: Object.freeze({ x: 1.86, y: 1.52, z: 4.42 }),
    wheelbaseM: 2.67,
    wheelRadiusM: 0.35,
    cabinLengthScale: 0.54,
    cabinHeightM: 0.61,
    cabinOffsetZ: -0.08,
    roofLengthScale: 0.48,
  }),
  "compact-van": Object.freeze({
    bodySizeM: Object.freeze({ x: 1.88, y: 1.74, z: 4.62 }),
    wheelbaseM: 2.78,
    wheelRadiusM: 0.33,
    cabinLengthScale: 0.67,
    cabinHeightM: 0.75,
    cabinOffsetZ: -0.18,
    roofLengthScale: 0.62,
  }),
  "older-boxy-car": Object.freeze({
    bodySizeM: Object.freeze({ x: 1.7, y: 1.32, z: 4.24 }),
    wheelbaseM: 2.51,
    wheelRadiusM: 0.3,
    cabinLengthScale: 0.5,
    cabinHeightM: 0.5,
    cabinOffsetZ: 0,
    roofLengthScale: 0.45,
  }),
});

export const MAPPED_ROAD_VEHICLE_FAMILY_LABELS: Readonly<
  Record<RuntimeParkedVehicleFamily, string>
> = Object.freeze({
  "compact-hatch": "Compact hatch",
  "estate-wagon": "Estate wagon",
  "ordinary-sedan": "Sedan",
  "small-crossover": "Small crossover",
  "compact-van": "Compact van",
  "older-boxy-car": "Older boxy car",
});

export const ROAD_VEHICLE_BODY_COLORS_BY_ID: Readonly<Record<string, number>> =
  Object.freeze({
    "parked-vilnius-city-bus": 0xe9e5d7,
    "parked-vilnius-new-trolleybus": 0xe8e5dc,
    "parked-vilnius-old-trolleybus": 0xd9cfb9,
    "parked-hybrid-liftback": 0xc8d0cb,
    "parked-german-sports-sedan": 0x293238,
    "cathedral-police-patrol-wagon": 0xe7ecec,
    "cathedral-police-support-van": 0xe7ecec,
    "cathedral-ambulance": 0xf1eee2,
    "cathedral-fire-rescue-engine": 0xc8261f,
  });

const DRIVABLE_IDS = new Set([
  "parked-vilnius-city-bus",
  "parked-vilnius-new-trolleybus",
  "parked-vilnius-old-trolleybus",
  "parked-hybrid-liftback",
  "parked-german-sports-sedan",
  "cathedral-police-patrol-wagon",
  "cathedral-police-support-van",
  "cathedral-ambulance",
  "cathedral-fire-rescue-engine",
]);

export const VILNIUS_ROAD_VEHICLES: readonly Readonly<RoadVehicleSpec>[] =
  Object.freeze([
    transitVehicle(
      "parked-vilnius-city-bus",
      "Vilnius city bus",
      "city-bus",
      { x: 522.247, y: 15, z: 331.565 },
      { x: 2.32, y: 2.47, z: 11.28 },
      5.9,
      0.51,
    ),
    transitVehicle(
      "parked-vilnius-new-trolleybus",
      "New Vilnius trolleybus",
      "trolleybus",
      { x: 525.66, y: 15, z: 332.611 },
      { x: 2.32, y: 2.62, z: 11.28 },
      5.9,
      0.51,
    ),
    transitVehicle(
      "parked-vilnius-old-trolleybus",
      "Old Vilnius trolleybus",
      "trolleybus",
      { x: 529.073, y: 15, z: 333.787 },
      { x: 2.28, y: 2.58, z: 10.67 },
      5.6,
      0.49,
    ),
    roadVehicle(
      "parked-hybrid-liftback",
      "Hybrid liftback",
      { x: 532.107, y: 15, z: 334.805 },
      { x: 1.57, y: 0.95, z: 4.09 },
      2.70,
    ),
    roadVehicle(
      "parked-german-sports-sedan",
      "Sports sedan",
      { x: 534.382, y: 15, z: 335.568 },
      { x: 1.60, y: 0.92, z: 4.24 },
      2.82,
    ),
    roadVehicle(
      "cathedral-police-patrol-wagon",
      "Lithuanian police patrol wagon",
      { x: 594.6, y: 15, z: 345 },
      { x: 1.61, y: 0.94, z: 4.23 },
      2.69,
      {
        initialYawRadians: 3.141593,
        cameraTargetHeightOffsetM: 0.5,
        cameraDistanceM: 6.2,
        seatZ: 0.48,
        hasEmergencyEquipment: true,
      },
    ),
    roadVehicle(
      "cathedral-police-support-van",
      "Lithuanian police support van",
      { x: 598.2, y: 15, z: 345 },
      { x: 1.79, y: 1.31, z: 4.55 },
      2.93,
      {
        initialYawRadians: 3.141593,
        vehicleClass: "service-van",
        cameraTargetHeightOffsetM: 0.95,
        cameraDistanceM: 7.2,
        seatX: 0.43,
        seatY: 0.82,
        seatZ: 1.25,
        wheelRadiusM: 0.36,
        hasEmergencyEquipment: true,
      },
    ),
    roadVehicle(
      "cathedral-ambulance",
      "Lithuanian ambulance",
      { x: 602.2, y: 15, z: 345 },
      { x: 1.80, y: 1.76, z: 5.39 },
      3.64,
      {
        initialYawRadians: 3.141593,
        vehicleClass: "service-van",
        cameraTargetHeightOffsetM: 1.3,
        cameraDistanceM: 8.5,
        seatX: 0.44,
        seatY: 0.92,
        seatZ: 1.83,
        wheelRadiusM: 0.38,
        hasEmergencyEquipment: true,
      },
    ),
    fireRescueVehicle(
      "cathedral-fire-rescue-engine",
      "Lithuanian fire and rescue engine",
      { x: 607.2, y: 15, z: 345 },
      { x: 2.28, y: 2.51, z: 6.86 },
      3.95,
    ),
  ]);

/** Reviewed City Lab arrival in the open lane ahead of the hybrid. */
export const ROAD_VEHICLE_REVIEW_DESTINATION = Object.freeze({
  id: "road-vehicle-car-park",
  label: "Vehicles: car park",
  tileId: "2:1" as const,
  arrivalPosition: Object.freeze({ x: 531.25, z: 337.36 }),
  facingYaw: 2.82,
});

const COLLISIONS_BY_TILE = new Map<
  string,
  readonly Readonly<ParkedVehicleCollisionSpec>[]
>();
for (const collision of VILNIUS_PARKED_VEHICLE_COLLISIONS) {
  const entries = COLLISIONS_BY_TILE.get(collision.tileId) ?? [];
  COLLISIONS_BY_TILE.set(
    collision.tileId,
    Object.freeze([...entries, collision]),
  );
}

export function parkedVehicleCollisionsForTile(
  tileId: string,
): readonly Readonly<ParkedVehicleCollisionSpec>[] {
  return COLLISIONS_BY_TILE.get(tileId) ?? [];
}

export function staticParkedVehicleCollisionsForTile(
  tileId: string,
): readonly Readonly<ParkedVehicleCollisionSpec>[] {
  return parkedVehicleCollisionsForTile(tileId).filter(
    (vehicle) => !DRIVABLE_IDS.has(vehicle.id),
  );
}

function roadVehicle(
  id: string,
  label: string,
  initialPosition: Readonly<{ x: number; y: number; z: number }>,
  bodySizeM: Readonly<{ x: number; y: number; z: number }>,
  wheelbaseM: number,
  options: Readonly<{
    initialYawRadians?: number;
    vehicleClass?: "car" | "service-van";
    cameraTargetHeightOffsetM?: number;
    cameraDistanceM?: number;
    seatX?: number;
    seatY?: number;
    seatZ?: number;
    wheelRadiusM?: number;
    hasEmergencyEquipment?: boolean;
  }> = {},
): Readonly<RoadVehicleSpec> {
  const halfWidth = bodySizeM.x / 2;
  const seatX = options.seatX ?? 0.34;
  const seatY = options.seatY ?? 0.64;
  const seatZ = options.seatZ ?? 0.30;
  return Object.freeze({
    id,
    label,
    tileId: "2:1" as const,
    initialPosition: Object.freeze({ ...initialPosition }),
    initialYawRadians: options.initialYawRadians ?? -0.323876,
    bodySizeM: Object.freeze({ ...bodySizeM }),
    wheelbaseM,
    wheelRadiusM: options.wheelRadiusM ?? 0.31,
    vehicleClass: options.vehicleClass ?? "car",
    hasEmergencyEquipment: options.hasEmergencyEquipment ?? false,
    cameraTargetHeightOffsetM: options.cameraTargetHeightOffsetM ?? 0.42,
    cameraDistanceM: options.cameraDistanceM ?? 5.8,
    seats: Object.freeze([
      Object.freeze({
        id: "driver",
        role: "driver" as const,
        // Runtime +X is vehicle-right, so -X is the physical left-hand-drive side.
        localPosition: Object.freeze({ x: -seatX, y: seatY, z: seatZ }),
      }),
      Object.freeze({
        id: "front-passenger",
        role: "passenger" as const,
        localPosition: Object.freeze({ x: seatX, y: seatY, z: seatZ }),
      }),
    ]),
    exits: Object.freeze([
      Object.freeze({
        id: "left-front",
        localPosition: Object.freeze({ x: -halfWidth - 0.75, y: 0, z: seatZ }),
      }),
      Object.freeze({
        id: "right-front",
        localPosition: Object.freeze({ x: halfWidth + 0.75, y: 0, z: seatZ }),
      }),
    ]),
  });
}

export function mappedParkedRoadVehicle(
  id: string,
  tileId: `${number}:${number}`,
  initialPosition: Readonly<{ x: number; y: number; z: number }>,
  initialYawRadians: number,
  proceduralBodyColorHex: number,
  proceduralFamily: RuntimeParkedVehicleFamily = "ordinary-sedan",
): Readonly<RoadVehicleSpec> {
  const profile = MAPPED_ROAD_VEHICLE_FAMILY_PROFILES[proceduralFamily];
  const bodySizeM = profile.bodySizeM;
  const halfWidth = bodySizeM.x / 2;
  const seatY = Math.min(0.78, Math.max(0.64, bodySizeM.y * 0.5));
  const seatZ = Math.min(0.42, bodySizeM.z * 0.075);
  return Object.freeze({
    id,
    label: MAPPED_ROAD_VEHICLE_FAMILY_LABELS[proceduralFamily],
    tileId,
    initialPosition: Object.freeze({ ...initialPosition }),
    initialYawRadians,
    bodySizeM,
    wheelbaseM: profile.wheelbaseM,
    wheelRadiusM: profile.wheelRadiusM,
    vehicleClass: "car" as const,
    hasEmergencyEquipment: false,
    proceduralBodyColorHex,
    proceduralFamily,
    cameraTargetHeightOffsetM: 0.45,
    cameraDistanceM: 5.9,
    seats: Object.freeze([
      Object.freeze({
        id: "driver",
        role: "driver" as const,
        localPosition: Object.freeze({ x: -0.34, y: seatY, z: seatZ }),
      }),
      Object.freeze({
        id: "front-passenger",
        role: "passenger" as const,
        localPosition: Object.freeze({ x: 0.34, y: seatY, z: seatZ }),
      }),
    ]),
    exits: Object.freeze([
      Object.freeze({
        id: "left-front",
        localPosition: Object.freeze({ x: -halfWidth - 0.75, y: 0, z: seatZ }),
      }),
      Object.freeze({
        id: "right-front",
        localPosition: Object.freeze({ x: halfWidth + 0.75, y: 0, z: seatZ }),
      }),
    ]),
  });
}

function fireRescueVehicle(
  id: string,
  label: string,
  initialPosition: Readonly<{ x: number; y: number; z: number }>,
  bodySizeM: Readonly<{ x: number; y: number; z: number }>,
  wheelbaseM: number,
): Readonly<RoadVehicleSpec> {
  const side = bodySizeM.x / 2 + 0.9;
  return Object.freeze({
    id,
    label,
    tileId: "2:1" as const,
    initialPosition: Object.freeze({ ...initialPosition }),
    initialYawRadians: 3.141593,
    bodySizeM: Object.freeze({ ...bodySizeM }),
    wheelbaseM,
    wheelRadiusM: 0.5,
    vehicleClass: "fire-engine" as const,
    hasEmergencyEquipment: true,
    cameraTargetHeightOffsetM: 1.9,
    cameraDistanceM: 12,
    seats: Object.freeze([
      Object.freeze({
        id: "driver",
        role: "driver" as const,
        localPosition: Object.freeze({ x: -0.58, y: 1.25, z: 2.48 }),
      }),
      Object.freeze({
        id: "front-passenger",
        role: "passenger" as const,
        localPosition: Object.freeze({ x: 0.58, y: 1.25, z: 2.48 }),
      }),
      Object.freeze({
        id: "rear-left",
        role: "passenger" as const,
        localPosition: Object.freeze({ x: -0.58, y: 1.25, z: 1.15 }),
      }),
      Object.freeze({
        id: "rear-right",
        role: "passenger" as const,
        localPosition: Object.freeze({ x: 0.58, y: 1.25, z: 1.15 }),
      }),
    ]),
    exits: Object.freeze([
      Object.freeze({
        id: "driver-left",
        localPosition: Object.freeze({ x: -side, y: 0, z: 2.48 }),
      }),
      Object.freeze({
        id: "front-right",
        localPosition: Object.freeze({ x: side, y: 0, z: 2.48 }),
      }),
      Object.freeze({
        id: "rear-left",
        localPosition: Object.freeze({ x: -side, y: 0, z: 1.15 }),
      }),
      Object.freeze({
        id: "rear-right",
        localPosition: Object.freeze({ x: side, y: 0, z: 1.15 }),
      }),
    ]),
  });
}

function transitVehicle(
  id: string,
  label: string,
  vehicleClass: "city-bus" | "trolleybus",
  initialPosition: Readonly<{ x: number; y: number; z: number }>,
  bodySizeM: Readonly<{ x: number; y: number; z: number }>,
  wheelbaseM: number,
  wheelRadiusM: number,
): Readonly<RoadVehicleSpec> {
  const halfWidth = bodySizeM.x / 2;
  return Object.freeze({
    id,
    label,
    tileId: "2:1" as const,
    initialPosition: Object.freeze({ ...initialPosition }),
    initialYawRadians: -0.323876,
    bodySizeM: Object.freeze({ ...bodySizeM }),
    wheelbaseM,
    wheelRadiusM,
    vehicleClass,
    hasEmergencyEquipment: false,
    cameraTargetHeightOffsetM: 2.25,
    cameraDistanceM: vehicleClass === "city-bus" ? 23.5 : 24,
    seats: Object.freeze([
      Object.freeze({
        id: "driver",
        role: "driver" as const,
        localPosition: Object.freeze({ x: -0.76, y: 0.92, z: 4.35 }),
      }),
      Object.freeze({
        id: "front-passenger",
        role: "passenger" as const,
        localPosition: Object.freeze({ x: 0.72, y: 0.92, z: 3.25 }),
      }),
      Object.freeze({
        id: "mid-passenger",
        role: "passenger" as const,
        localPosition: Object.freeze({ x: -0.72, y: 0.92, z: 0.25 }),
      }),
      Object.freeze({
        id: "rear-passenger",
        role: "passenger" as const,
        localPosition: Object.freeze({ x: 0.72, y: 1.02, z: -3.35 }),
      }),
    ]),
    exits: Object.freeze([
      Object.freeze({
        id: "driver-left",
        localPosition: Object.freeze({ x: -halfWidth - 0.85, y: 0, z: 4.15 }),
      }),
      Object.freeze({
        id: "front-right",
        localPosition: Object.freeze({ x: halfWidth + 0.85, y: 0, z: 3.35 }),
      }),
      Object.freeze({
        id: "middle-right",
        localPosition: Object.freeze({ x: halfWidth + 0.85, y: 0, z: 0.15 }),
      }),
      Object.freeze({
        id: "rear-right",
        localPosition: Object.freeze({ x: halfWidth + 0.85, y: 0, z: -3.55 }),
      }),
    ]),
  });
}

function motionTuning(
  maximumForwardSpeedMps: number,
  maximumReverseSpeedMps: number,
  accelerationMps2: number,
  reverseAccelerationMps2: number,
  brakingMps2: number,
  coastingDragMps2: number,
  maximumSteerRadians: number,
  steeringResponseRadiansPerSecond: number,
  steeringSelfCenterResponsePerSecond: number,
  steeringFadeStartSpeedMps: number,
  steeringFadeEndSpeedMps: number,
  highSpeedSteeringScale: number,
  maximumYawRateRadiansPerSecond: number,
  handbrakeDecelerationMps2: number,
  handbrakeSteeringScale: number,
): Readonly<RoadVehicleMotionTuning> {
  return Object.freeze({
    maximumForwardSpeedMps,
    maximumReverseSpeedMps,
    accelerationMps2,
    reverseAccelerationMps2,
    brakingMps2,
    coastingDragMps2,
    maximumSteerRadians,
    steeringResponseRadiansPerSecond,
    steeringSelfCenterResponsePerSecond,
    steeringFadeStartSpeedMps,
    steeringFadeEndSpeedMps,
    highSpeedSteeringScale,
    maximumYawRateRadiansPerSecond,
    handbrakeDecelerationMps2,
    handbrakeSteeringScale,
  });
}

function parkedVehicle(
  id: string,
  origin: Readonly<{ x: number; y: number; z: number }>,
  bodySizeM: Readonly<{ x: number; y: number; z: number }>,
  rotationYRadians = -0.323876,
): Readonly<ParkedVehicleCollisionSpec> {
  return Object.freeze({
    id,
    tileId: "2:1" as const,
    origin: Object.freeze({ ...origin }),
    rotationYRadians,
    bodySizeM: Object.freeze({ ...bodySizeM }),
  });
}
